import React, { useEffect, useState } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { api } from '../../server';
import { getConfig } from '../../utils/getClient';
import { useHospital } from '../../contexts/HospitalContext';
import Logo from '../../assets/erpsaude.png';
import '../../App.css';
import { format, differenceInSeconds, isValid } from 'date-fns';

function toLabel(slug) {
  return slug.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function Clock() {
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  return (
    <time dateTime={now.toISOString()} className="panel-clock" aria-label="Horário atual">
      {format(now, 'HH:mm:ss')}
    </time>
  );
}

function calculateTimeElapsed(horario, now) {
  const [hours, minutes, seconds] = horario.split(':').map(Number);
  const startTime = new Date(now);
  startTime.setHours(hours, minutes, seconds, 0);
  if (startTime > now) startTime.setDate(startTime.getDate() - 1);
  const total = differenceInSeconds(now, startTime);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function getPacienteLabel(row) {
  return row.NOME || row.DESC_SENHA || '-';
}

function isAtendimentoPorSenha(row) {
  return !row.NOME && Boolean(row.DESC_SENHA);
}

function formatDataTriagem(row) {
  const data = new Date(row.DATA_ATENDIMENTO || row.DATA_GEROU);
  return isValid(data) ? format(data, 'dd/MM/yyyy') : '-';
}

function formatHoraTriagem(row) {
  if (row.HORARIO) {
    const horario = new Date(row.HORARIO);
    return isValid(horario) ? format(horario, 'HH:mm:ss') : '-';
  }

  if (row.HORA_GEROU) {
    const match = String(row.HORA_GEROU).match(/T(\d{2}:\d{2}:\d{2})/);
    if (match) return match[1];

    const horaGerou = new Date(row.HORA_GEROU);
    return isValid(horaGerou) ? format(horaGerou, 'HH:mm:ss') : '-';
  }

  return '-';
}

function TablePainel({ token }) {
  const [painel, setPainel] = useState([]);
  const [now, setNow] = useState(new Date());

  // Atualiza o relógio local a cada segundo (sem fazer nova request)
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  // Busca dados a cada 60s
  useEffect(() => {
    const config = getConfig(token);

    const fetchPainel = async () => {
      try {
        const response = await api.get('/painel/painel_triagem', config);
        if (response.data) setPainel(response.data);
      } catch (error) {
        console.error('Erro ao buscar painel de triagem:', error.message);
      }
    };

    fetchPainel();
    const intervalId = setInterval(fetchPainel, 60000);
    return () => clearInterval(intervalId);
  }, [token]);

  if (painel.length === 0) {
    return (
      <div className="panel-empty" role="status" aria-live="polite">
        <p>Nenhum paciente aguardando triagem</p>
      </div>
    );
  }

  const temAtendimentoPorSenha = painel.some(isAtendimentoPorSenha);

  return (
    <div className="panel-table-wrap">
      <div className="panel-count-bar" role="status" aria-live="polite">
        <span className="panel-count-number">{painel.length}</span>
        <span className="panel-count-label">
          paciente{painel.length !== 1 ? 's' : ''} aguardando triagem
        </span>
      </div>

      <TableContainer component={Paper} elevation={0}>
        <Table aria-label="Pacientes aguardando triagem">
          <TableHead>
            <TableRow>
              <TableCell>{temAtendimentoPorSenha ? 'Paciente/Senha' : 'Paciente'}</TableCell>
              <TableCell>Data</TableCell>
              <TableCell>Chegada</TableCell>
              <TableCell>Tempo de espera</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {painel.map((row, index) => {
              const paciente = getPacienteLabel(row);
              const data = formatDataTriagem(row);
              const horario = formatHoraTriagem(row);
              const tempoEspera = horario === '-' ? '-' : calculateTimeElapsed(horario, now);
              return (
                <TableRow key={`${paciente}-${index}`}>
                  <TableCell className="cell-nome">{paciente}</TableCell>
                  <TableCell>{data}</TableCell>
                  <TableCell>{horario}</TableCell>
                  <TableCell className="cell-tempo">{tempoEspera}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
}

export default function PainelTriagem() {
  const { hospital, token } = useHospital();

  return (
    <div className="panel-container">
      <header className="panel-header" role="banner">
        <img src={Logo} alt="ERPSaúde" className="panel-logo" />
        <div className="panel-title-wrap">
          <h1 className="panel-title">Painel de Triagem</h1>
          <span className="panel-hospital">{toLabel(hospital)}</span>
        </div>
        <Clock />
      </header>

      <main className="panel-main">
        <TablePainel token={token} />
      </main>
    </div>
  );
}
